import { useEffect, useRef } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useTheme } from "@/hooks/useTheme";

export function OpArtBackground({ scrollProgress }: { scrollProgress: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const reducedMotion = useReducedMotion();
  const { theme } = useTheme();
  const scrollRef = useRef(scrollProgress);
  scrollRef.current = scrollProgress;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let time = 0;

    const isMobile = window.innerWidth < 768;
    const numCircles = isMobile ? 60 : 100;
    const secondaryStep = isMobile ? 0 : 4; // 0 = hidden, 4 = every 4th circle
    const dpr = Math.min(window.devicePixelRatio, 2);
    const frameInterval = isMobile ? 33 : 22;
    let lastFrameTime = 0;

    const isDark = document.documentElement.classList.contains("dark");
    const bgFill = isDark ? "#0a0a0a" : "#FFFFFF";
    const strokeColor = isDark ? "255, 255, 255" : "0, 0, 0";

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener("resize", resize);

    const draw = (now: number) => {
      if (frameInterval && now - lastFrameTime < frameInterval) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      lastFrameTime = now;

      ctx.fillStyle = bgFill;
      ctx.fillRect(0, 0, width, height);

      const maxRadius = Math.max(width, height) * 0.8;
      const radiusStep = maxRadius / numCircles;

      const freqX = Math.cos(time * 0.0005) * 2;
      const freqY = Math.sin(time * 0.0004) * 2;

      ctx.lineWidth = 1;

      // Primary group — center
      for (let i = 0; i < numCircles; i++) {
        const t = i / numCircles;
        const offsetX =
          Math.cos(t * Math.PI * freqX + time * 0.001) * (width * 0.15);
        const offsetY =
          Math.sin(t * Math.PI * freqY - time * 0.0015) * (height * 0.15);

        const cx = width / 2 + offsetX;
        const cy = height / 2 + offsetY;
        const r = i * radiusStep;

        const alpha = 0.05 + (1 - t) * 0.15;
        ctx.strokeStyle = `rgba(${strokeColor}, ${alpha})`;

        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Secondary group — offset right (hidden on mobile, every 4th on desktop)
      if (secondaryStep > 0) {
        for (let i = 0; i < numCircles; i += secondaryStep) {
          const t = i / numCircles;
          const offsetX =
            Math.cos(t * Math.PI * 2 - time * 0.002) * (width * 0.05);
          const offsetY =
            Math.cos(t * Math.PI * 2 + time * 0.001) * (height * 0.05);

          const cx = width * 0.7 + offsetX;
          const cy = height * 0.5 + offsetY;
          const r = i * radiusStep * 0.8;

          ctx.strokeStyle = `rgba(${strokeColor}, 0.08)`;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      if (!reducedMotion) {
        time += 16;
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [reducedMotion, theme]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        pointerEvents: "none",
        mixBlendMode: "var(--bg-blend-mode)" as React.CSSProperties["mixBlendMode"],
      }}
      aria-hidden="true"
    />
  );
}
